<template>
  <BasicSmModal
    v-if="!noGallery || (noGallery && showCropping)"
    @close="closeDisplay"
  >
    <div v-if="!showCropping">
      <p class="text-gray-500 mb-4 -my-4">
        {{ $t('imageHandler.choosePictureTitle') }}
      </p>
      <div
        @click="showCollectionPicker = !showCollectionPicker"
        class="flex cursor-pointer"
        click-outside-ignore-countrypicker
      >
        <p v-if="imageCollectionsId" class="text-sm font-medium mr-1">
          {{ $t(`collections.${imageCollectionsId}.name`) }}
        </p>
        <icon name="chevron-down" />
      </div>
      <div v-if="showCollectionPicker" class="relative">
        <div
          class="absolute w-[20rem] top-1 left-0 bg-white px-3 py-2 border rounded-xl shadow-md max-h-40 overflow-y-auto z-20"
          click-outside-ignore-countrypicker-target
          v-click-outside="
            () => {
              showCollectionPicker = false;
            }
          "
        >
          <p
            v-for="collectionId in Object.keys(imageCollections)"
            :key="collectionId"
            @click="
              () => {
                imageCollectionsId = collectionId;
                showCollectionPicker = false;
                scrollableDiv.scrollTop = 0;
              }
            "
            class="text-sm font-medium my-1 hover:bg-gray-light rounded-md cursor-pointer"
          >
            {{ $t(`collections.${collectionId}.name`) }}
          </p>
        </div>
      </div>
      <div class="border-b mt-2 mb-3"></div>
      <div
        class="grid grid-cols-4 h-64 overflow-y-scroll overflow-x-hidden gap-0.5 p-2"
        ref="scrollableDiv"
      >
        <div
          v-for="image in imageCollections[imageCollectionsId]"
          class="w-full"
        >
          <img
            @click="
              currentImage != image
                ? (currentImage = image)
                : (currentImage = null)
            "
            :src="image.path"
            class="w-full cursor-pointer rounded-md"
            :class="{
              'border-blue-700 border-4': currentImage == image,
              'border-transparent border-4': currentImage != image,
            }"
          />
        </div>
      </div>

      <div class="flex items-center gap-3 mt-5">
        <div class="grow"></div>
        <button
          class="bg-white text-sm border shadow-sm w-max font-medium rounded-lg cursor-pointer py-1.5 px-2"
          @click="$refs.templatePictureInput.click()"
        >
          <icon name="image" class="mr-1.5" />{{
            $t('imageHandler.uploadButton')
          }}
        </button>

        <button
          @click="selectImage"
          class="bg-secondary text-white text-sm shadow-sm font-medium rounded-lg cursor-pointer py-1.5 px-2.5 ml-auto"
          :class="{
            'pointer-events-none opacity-50': !currentImageFile,
          }"
        >
          {{ $t('imageHandler.selectButton') }}
        </button>
      </div>
    </div>
    <div v-else>
      <p class="text-gray-500 mb-4 -my-4">
        {{ $t('imageHandler.cropTitle') }}
      </p>
      <cropper
        ref="cropper"
        class="cropper"
        :class="cropperClass"
        :src="currentImagePreview"
        :stencil-props="{
          aspectRatio: aspectRatio
            ? aspectRatio
            : type == 'Membership'
            ? 856 / 540
            : 1 / 1,
        }"
      />
      <div class="flex items-center gap-3 mt-5">
        <button
          @click="selectCroppedImage"
          class="bg-secondary text-white text-sm shadow-sm font-medium rounded-lg cursor-pointer py-1.5 px-2.5 ml-auto"
        >
          {{ $t('imageHandler.confirmButton') }}
        </button>
      </div>
    </div>
    <input
      type="file"
      @change="importTemplatePictureInput"
      ref="templatePictureInput"
      class="hidden"
    />
  </BasicSmModal>
  <input
    v-else
    type="file"
    @change="importTemplatePictureInput"
    ref="templatePictureInput"
    class="hidden"
  />
</template>
<script>
import { ref, watch, inject, computed, onMounted, onUnmounted } from 'vue';
import { Cropper } from 'vue-advanced-cropper';
import { useI18n } from 'vue-i18n';
import 'vue-advanced-cropper/dist/style.css';
import BasicSmModal from './BasicSmModal.vue';
import collections from '../../assets/badges/collections.json';

export default {
  props: {
    type: String,
    noGallery: Boolean,
    aspectRatio: Number,
    cropperShape: String,
  },
  setup(props, context) {
    const utils = inject('utils');
    const { t } = useI18n();

    const showCropping = ref(false);
    const showCollectionPicker = ref(false);
    const imageCollections = ref([]);
    const imageCollectionsId = ref(null);
    const currentImage = ref(null);
    const currentImageFile = ref(null);
    const currentImagePreview = ref(null);
    const currentImageMessage = ref(null);
    const cropper = ref();
    const templatePictureInput = ref();
    const MAX_SIZE_IN_BYTES = 10 * 1024 * 1024;
    const scrollableDiv = ref();

    const cropperClass = computed(() => {
      if (props.type == 'Role' && props.cropperShape) {
        return `role-${props.cropperShape}-cropper`;
      }
      return '';
    });

    if (props.type) {
      // can't put variable for path inside context method
      let imageContext;
      let currentCollections;

      if (
        props.type == 'Community' ||
        props.type == 'Basic' ||
        props.type == 'Role'
      ) {
        imageContext = require.context(`/assets/badges/basic-community`, false);
        currentCollections = collections.reduce((array, collection) => {
          if (collection.scope == 'basic-community') array[collection.id] = [];
          return array;
        }, []);
      } else if (props.type == 'Membership') {
        imageContext = require.context(`/assets/badges/membership`, false);
        currentCollections = collections.reduce((array, collection) => {
          if (collection.scope == 'membership') array[collection.id] = [];
          return array;
        }, []);
      } else if (props.type == 'Participation' || props.type == 'Ticket') {
        imageContext = require.context(`/assets/badges/participation`, false);
        currentCollections = collections.reduce((array, collection) => {
          if (collection.scope == 'participation') array[collection.id] = [];
          return array;
        }, []);
      }

      const formattedImageContext = imageContext
        .keys()
        .reduce((array, path) => {
          return array.concat({
            path: imageContext(path),
            name: path.substring(path.lastIndexOf('/') + 1),
          });
        }, []);

      for (let image of formattedImageContext) {
        const collectionId = image.name.split('_')[0];
        if (Object.keys(currentCollections).indexOf(collectionId) != -1) {
          currentCollections[collectionId].push(image);
        }
      }

      imageCollections.value = currentCollections;
      imageCollectionsId.value = Object.keys(currentCollections)[0];
    }

    watch(currentImage, () => {
      if (!currentImage.value) return;
      convertImageToBase64(currentImage.value.path).then((data) => {
        currentImageFile.value = utils.base64ToFile(
          data,
          currentImage.value.name
        );
      });
    });

    onMounted(() => {
      if (props.noGallery) {
        templatePictureInput.value.click();
        window.addEventListener('focus', onWindowFocus, { once: true });
        window.document.addEventListener('mousemove', onWindowFocus, {
          once: true,
        });
      }
    });

    onUnmounted(() => {
      // Clean up event listeners when the component is unmounted
      window.removeEventListener('focus', onWindowFocus);
      window.document.removeEventListener('mousemove', onWindowFocus);
    });

    function onWindowFocus() {
      setTimeout(() => {
        if (currentImageFile.value == null) {
          if (props.noGallery) {
            context.emit('close');
          }
        }
      }, 1000);
    }

    async function importTemplatePictureInput(event) {
      const file = event.target.files[0];

      if (['image/png', 'image/jpeg'].indexOf(file['type']) == -1) {
        currentImageFile.value = null;
        currentImagePreview.value = null;
        utils.showToastMessageError(t('imageHandler.errors.fileFormatInvalid'));
        if (props.noGallery) context.emit('close');
        return;
      }

      if (file.size > MAX_SIZE_IN_BYTES) {
        currentImageFile.value = null;
        currentImagePreview.value = null;
        utils.showToastMessageError(t('imageHandler.errors.sizeExceeded'));
        if (props.noGallery) context.emit('close');
        return;
      }

      currentImageFile.value = file;
      currentImageMessage.value = null;
      const reader = new FileReader();
      reader.readAsDataURL(currentImageFile.value);
      reader.onload = async (e) => {
        currentImagePreview.value = e.target.result;
        // context.emit(
        //   'select',
        //   currentImageFile.value,
        //   currentImagePreview.value
        // );
        showCropping.value = true;
      };
    }

    function uploadImage() {
      context.emit('upload');
    }

    function selectImage() {
      context.emit('select', currentImageFile.value, currentImage.value.path);
    }

    async function selectCroppedImage() {
      const result = cropper.value.getResult();

      currentImageFile.value = utils.base64ToFile(
        result.canvas.toDataURL(result.image.type),
        currentImageFile.value.name
      );

      const reader = new FileReader();
      reader.readAsDataURL(currentImageFile.value);
      reader.onload = async (e) => {
        currentImagePreview.value = e.target.result;
        context.emit(
          'select',
          currentImageFile.value,
          currentImagePreview.value
        );
      };
    }

    function closeDisplay() {
      context.emit('close');
    }

    async function convertImageToBase64(imagePath) {
      try {
        const response = await fetch(imagePath);
        const blob = await response.blob();
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = function () {
            resolve(reader.result);
          };
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
      } catch (error) {
        console.error('Error converting image to Base64:', error);
        return null;
      }
    }

    return {
      showCropping,
      showCollectionPicker,
      closeDisplay,
      uploadImage,
      selectImage,
      selectCroppedImage,
      imageCollections,
      imageCollectionsId,
      currentImage,
      currentImageFile,
      currentImagePreview,
      importTemplatePictureInput,
      cropper,
      cropperClass,
      templatePictureInput,
      scrollableDiv,
    };
  },
  components: {
    BasicSmModal,
    Cropper,
  },
};
</script>
<style scoped>
.role-shape_1-cropper::v-deep .vue-rectangle-stencil {
  background-image: url("data:image/svg+xml,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%3F%3E%3Csvg%20id%3D%22Calque_1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%20265.2%20265.46%22%3E%0A%20%20%3Cpath%20d%3D%22M128.81.39c5.1-1%2C10.5.3%2C14.9%2C2.9%2C11.5%2C6.5%2C19%2C18.3%2C30.8%2C24.4%2C14.3%2C6.3%2C30.7%2C1.4%2C45.2%2C6.9%2C5.3%2C1.9%2C9.4%2C6.4%2C11.2%2C11.7%2C4.9%2C14.1.6%2C29.8%2C6.3%2C43.7%2C6.2%2C12.8%2C19.3%2C20.6%2C25.7%2C33.2%2C2.9%2C5.6%2C3.1%2C12.6.3%2C18.3-6.2%2C13.1-19.7%2C20.9-25.9%2C33.9-5.5%2C13.7-1.5%2C29.1-6%2C43-1.8%2C5.6-6.1%2C10.5-11.8%2C12.5-14%2C5.2-29.7.9-43.7%2C6.3-13.9%2C6.4-21.8%2C21.2-36.1%2C26.9-6.1%2C2.5-13.1%2C1.2-18.6-2.1-11.1-6.5-18.5-17.8-29.9-24-14.4-6.7-31.1-1.6-45.8-7.2-5-2-9.1-6-10.9-11.1-5.4-14.5-.6-30.8-6.9-45.1-6.4-12.4-19.1-20.1-25.3-32.6-2.9-5.5-3.1-12.2-.4-17.8%2C6-13.2%2C19.5-21%2C25.9-33.9%2C5.8-13.9%2C1.6-29.6%2C6.3-43.7%2C1.9-5.9%2C6.7-10.6%2C12.6-12.5%2C13.7-4.5%2C28.7-.8%2C42.3-5.7%2C15.3-6.5%2C23.5-23.9%2C40-28.1h-.2v.1ZM129.71%2C14.89c-9.1%2C4.9-15.4%2C13.4-23.6%2C19.6-7%2C5.7-15.7%2C9.2-24.7%2C10.2-10%2C1.5-20.5.2-30.3%2C2.9-3.6.9-4.2%2C5-4.8%2C8.1-1.7%2C13.2%2C0%2C27.1-5.4%2C39.6-5.1%2C12.1-15.9%2C20.2-23.7%2C30.4-1.8%2C2.5-4%2C5.7-2.9%2C9%2C1.4%2C3.7%2C4.1%2C6.6%2C6.6%2C9.5%2C7.6%2C8.5%2C16.4%2C16.4%2C20.6%2C27.3%2C5%2C12.7%2C2.9%2C26.6%2C5.1%2C39.7.4%2C3.1%2C2%2C6.4%2C5.4%2C6.9%2C13.4%2C3%2C27.6.3%2C40.7%2C5.2%2C13%2C4.5%2C21.6%2C15.8%2C32%2C24%2C3%2C2.3%2C7.1%2C5.3%2C11%2C3%2C7.5-4.2%2C13.1-11%2C19.6-16.5%2C6-5.4%2C13-9.9%2C21-11.8%2C11.1-2.8%2C22.7-1.7%2C33.9-3.3%2C2.8-.5%2C6.5-1.3%2C7.3-4.6%2C2.4-8.2%2C1.6-16.9%2C2.4-25.3.6-7.8%2C2-15.7%2C5.9-22.6%2C5.6-10%2C14.7-17.3%2C21.7-26.3%2C1.7-2.4%2C3.8-5%2C3.4-8.2-.9-3.4-3.3-6.3-5.5-9-6-7-13-13.2-18-21.1-4.1-6.2-6.2-13.6-7.1-20.9-1.3-9.5-.2-19.3-2.5-28.7-.6-3.1-3.5-4.9-6.5-5.2-13.7-2.4-28.4%2C0-41.4-5.7-11.6-4.9-19.6-15-29.1-22.8-3.1-2.4-7-5.5-11.1-3.5h0v.1Z%22%20%2F%3E%0A%3C%2Fsvg%3E") !important;
  background-size: cover !important;
}
.role-shape_2-cropper::v-deep .vue-rectangle-stencil {
  background-image: url("data:image/svg+xml,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%3F%3E%3Csvg%20id%3D%22Calque_1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%20265.2%20265.46%22%3E%0A%20%20%3Cpath%20d%3D%22M126.69%2C5.51c10.8-1.9%2C22.6%2C1.6%2C30.3%2C9.5%2C31.3%2C31.3%2C62.7%2C62.6%2C94%2C94%2C12.5%2C12.6%2C12.4%2C34.9%2C0%2C47.5-28.8%2C28.9-57.7%2C57.7-86.5%2C86.5-4.5%2C4.3-8.4%2C9.1-13.7%2C12.4-10.6%2C6.6-24.8%2C6.7-35.5.4-4.8-2.7-8.5-6.9-12.4-10.7-29.2-29.2-58.3-58.3-87.5-87.5-4.3-4.1-7.4-9.4-9.1-15.1-3.7-12.1%2C0-25.8%2C8.9-34.6%2C31.3-31.3%2C62.6-62.8%2C94-93.9%2C4.8-4.4%2C10.9-7.4%2C17.4-8.5h.1ZM125.59%2C20.41c-5%2C1.7-8.6%2C5.9-12.2%2C9.6-28%2C28-56%2C56-84%2C84-3.3%2C3.4-7.2%2C6.6-8.8%2C11.2-3.1%2C7.5-1.1%2C16.8%2C4.9%2C22.3%2C29.3%2C29.3%2C58.7%2C58.7%2C88%2C88%2C3.5%2C3.5%2C6.9%2C7.6%2C11.8%2C9.3%2C7.8%2C3.1%2C17%2C.7%2C22.7-5.3%2C30.7-30.7%2C61.4-61.3%2C92-92%2C5.6-5.5%2C7.7-14.1%2C5.1-21.5-1.6-5.4-6.3-9.1-10.1-13-27.8-27.9-55.7-55.7-83.5-83.5-2.5-2.5-4.8-5.2-7.8-7.1-5.3-3.4-12.1-4.1-18-2h-.1Z%22%20%2F%3E%0A%3C%2Fsvg%3E") !important;
  background-size: cover !important;
}
.role-shape_3-cropper::v-deep .vue-rectangle-stencil {
  background-image: url("data:image/svg+xml,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%3F%3E%3Csvg%20id%3D%22Calque_1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%20265.2%20265.46%22%3E%0A%20%20%3Cpath%20d%3D%22M128.85.34c4.6-.9%2C9.5%2C0%2C13.5%2C2.5%2C28.1%2C18.3%2C59.2%2C31.8%2C91.4%2C40.8%2C5.5%2C1.3%2C11.6%2C2.7%2C15.4%2C7.4%2C4%2C4.2%2C4.5%2C10.3%2C4.3%2C15.8v50c-.2%2C21.2-4.5%2C42.4-13.5%2C61.6-13.4%2C28.8-36.7%2C52-63.7%2C68.3-10.9%2C6.6-22.5%2C12.2-34.4%2C16.6-4.8%2C1.9-10.1%2C3-15.1%2C1.2-19.6-6.8-38.2-16.8-54.7-29.3-24.4-18.4-44-43.8-53.2-73.2-4.6-14.2-6.9-29.2-7.1-44.1-.1-18.4%2C0-36.8%2C0-55.1-.2-7.9%2C5.6-15.3%2C13.2-17.3%2C31.7-8.2%2C62.4-20.7%2C90.3-37.7%2C4.4-2.6%2C8.5-6.2%2C13.6-7.3v-.2ZM129.95%2C15.24c-30.8%2C19.9-65%2C34.4-100.5%2C43.7-1.8.4-3.7%2C1.7-3.4%2C3.8v55.1c.3%2C26.2%2C8.1%2C52.5%2C23.4%2C74%2C19.5%2C27.6%2C48.9%2C47.1%2C80.3%2C58.7%2C3.1%2C1.5%2C6.3-.3%2C9.3-1.3%2C31.1-12.2%2C59.9-32.2%2C78.7-60.2%2C11.2-16.7%2C18.1-36.1%2C20.5-56%2C1.4-10.7%2C1-21.4%2C1-32.2v-37.1c.1-1.9-.6-4.3-2.8-4.6-35.8-9.3-70.3-23.9-101.4-44-1.5-1.2-3.6-1-5.1.1Z%22%20%2F%3E%0A%3C%2Fsvg%3E") !important;
  background-size: cover !important;
}
.role-shape_4-cropper::v-deep .vue-rectangle-stencil {
  background-image: url("data:image/svg+xml,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%3F%3E%3Csvg%20id%3D%22Calque_1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%20265.2%20265.46%22%3E%0A%20%20%3Cpath%20d%3D%22M123.63%2C1.66c10.5-2.2%2C21.7-.4%2C30.9%2C5.1%2C25.5%2C14.7%2C51.1%2C29.5%2C76.6%2C44.3%2C10.7%2C6%2C18.4%2C16.9%2C20.7%2C28.9%2C1.1%2C5.3.8%2C10.7.8%2C16.1v81c0%2C11.6-4.8%2C23.2-13.3%2C31.2-3.4%2C3.4-7.5%2C5.9-11.7%2C8.2-21.4%2C12.3-42.7%2C24.7-64%2C37-7.2%2C4.1-14.3%2C9-22.6%2C10.4-10.1%2C2.1-21%2C.3-29.9-4.9-25.8-14.9-51.6-29.8-77.4-44.7-12.7-7.4-21.1-21.6-21.2-36.3v-90c0-14.8%2C8.2-29.2%2C21-36.7%2C25.5-14.8%2C51.1-29.5%2C76.6-44.3%2C4.2-2.4%2C8.7-4.4%2C13.4-5.4l.1.1ZM126.73%2C15.56c-6.4%2C1.3-11.7%2C5.4-17.4%2C8.5-20.5%2C11.8-41%2C23.7-61.5%2C35.5-4.8%2C2.8-9.9%2C5.2-13.6%2C9.4-5%2C5.4-7.5%2C12.7-7.4%2C20v88.1c0%2C7.4%2C2.8%2C14.9%2C8.1%2C20.2%2C3.5%2C3.7%2C8.2%2C6%2C12.6%2C8.5%2C23.7%2C13.6%2C47.3%2C27.4%2C71%2C41%2C8.6%2C5%2C19.8%2C5%2C28.4%2C0%2C25.8-14.9%2C51.6-29.8%2C77.4-44.7%2C6.4-3.6%2C11.2-9.9%2C13.1-17%2C1.5-4.9%2C1-10%2C1-15.1v-82c0-9.8-5.4-19.3-13.8-24.3-25.8-14.9-51.6-29.8-77.4-44.7-6.1-3.6-13.6-4.8-20.6-3.3l.1-.1Z%22%20%2F%3E%0A%3C%2Fsvg%3E") !important;
  background-size: cover !important;
}
.role-shape_5-cropper::v-deep .vue-rectangle-stencil {
  background-image: url("data:image/svg+xml,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%3F%3E%3Csvg%20id%3D%22Calque_1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%20265.2%20265.46%22%3E%0A%20%20%3Cpath%20d%3D%22M110.42%2C10.32c9.29-7.96%2C22.99-10.18%2C34.39-5.79%2C9.6%2C3.43%2C15.91%2C11.9%2C23.33%2C18.42%2C9.58-.83%2C19.34-3.91%2C28.91-1.33%2C12.67%2C3.05%2C23%2C13.87%2C25.57%2C26.63%2C1.08%2C5.54%2C2.05%2C11.11%2C3.14%2C16.65%2C7.99%2C4.76%2C17.23%2C7.99%2C23.47%2C15.18%2C7.6%2C8.46%2C10.6%2C20.81%2C7.64%2C31.8-1.97%2C7.44-6.64%2C13.86-8.96%2C21.13%2C3.2%2C8.1%2C8.45%2C15.53%2C9.74%2C24.3%2C1.93%2C12.74-3.93%2C26.33-14.59%2C33.59-5.57%2C3.58-11.57%2C6.42-17.29%2C9.73-2.2%2C9.45-2.41%2C19.72-7.81%2C28.1-6.61%2C10.61-19.3%2C17.12-31.79%2C16.06-6.02-.55-11.99-1.56-18-2.25-7.21%2C6.27-13.25%2C14.37-22.38%2C18.01-11.87%2C5-26.46%2C2.64-36.05-5.99-4.23-3.95-8.51-7.86-12.54-12.01-9.36.73-18.8%2C3.67-28.18%2C1.53-12.11-2.67-22.53-12.21-25.81-24.24-1.6-6.34-2.49-12.83-3.81-19.22-7.95-4.79-17.17-8.04-23.42-15.2-8.7-9.43-11.15-23.93-6.41-35.81%2C2.49-5.69%2C5.29-11.25%2C7.93-16.88-4.01-9.28-10.28-18.15-10.27-28.63-.44-11.16%2C4.98-22.35%2C14.02-28.91%2C5.75-3.93%2C12.11-6.85%2C18.16-10.27%2C1.21-6.04%2C2.1-12.14%2C3.48-18.14%2C2.97-12.26%2C13.22-22.37%2C25.5-25.23%2C9.45-2.44%2C19.05.51%2C28.5%2C1.42%2C4.68-4.04%2C8.82-8.65%2C13.53-12.65ZM125.12%2C17.94c-4.76%2C1.88-8.24%2C5.78-11.88%2C9.21-3.7%2C3.7-7.64%2C7.16-11.41%2C10.8-7.86-.97-15.7-2.17-23.57-3.03-9.5-1.03-19.07%2C5.61-21.41%2C14.87-1.8%2C8.06-3.07%2C16.23-4.69%2C24.32-6.83%2C3.85-13.8%2C7.45-20.61%2C11.35-6.49%2C3.71-10.55%2C11.2-10.08%2C18.67.05%2C4.8%2C2.56%2C9.02%2C4.48%2C13.26%2C2.5%2C5.09%2C4.79%2C10.27%2C7.33%2C15.35-2.52%2C5.03-4.81%2C10.17-7.26%2C15.23-1.89%2C4.19-4.42%2C8.31-4.55%2C13.04-.61%2C7.75%2C3.68%2C15.57%2C10.5%2C19.28%2C6.69%2C3.66%2C13.35%2C7.39%2C20.11%2C10.95%2C1.43%2C6.89%2C2.63%2C13.83%2C3.95%2C20.74%2C1.02%2C10.74%2C11.14%2C19.82%2C22.07%2C18.58%2C7.98-.81%2C15.9-2.11%2C23.87-3.02%2C5.73%2C5.56%2C11.53%2C11.05%2C17.37%2C16.48%2C7.7%2C6.91%2C20.58%2C6.38%2C27.72-1.11%2C5.48-5.08%2C10.76-10.37%2C16.31-15.39%2C7.96%2C1.01%2C15.91%2C2.23%2C23.89%2C3.06%2C9.48.87%2C18.93-5.89%2C21.11-15.16%2C1.74-7.98%2C3.05-16.05%2C4.6-24.07%2C6.99-3.82%2C14.01-7.6%2C20.94-11.51%2C8.8-5.09%2C12.38-17.13%2C7.89-26.23-3.25-6.98-6.59-13.92-9.89-20.87%2C2.93-6.07%2C5.73-12.21%2C8.7-18.26%2C1.71-3.54%2C3.22-7.33%2C3.12-11.32.06-7.06-3.8-14-9.89-17.57-6.19-3.6-12.58-6.84-18.83-10.34-.88-.6-2.13-1.03-2.2-2.26-1.57-7.48-2.72-15.05-4.3-22.53-2.14-9.39-11.6-16.47-21.24-15.56-8.05.81-16.04%2C2.08-24.06%2C3.07-5.56-5.41-11.22-10.73-16.84-16.09-5.62-5.17-14.16-6.73-21.25-3.94Z%22%20%2F%3E%0A%3C%2Fsvg%3E") !important;
  background-size: cover !important;
}
.role-shape_6-cropper::v-deep .vue-rectangle-stencil {
  background-image: url("data:image/svg+xml,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%3F%3E%3Csvg%20id%3D%22Calque_1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%20265.2%20265.46%22%3E%0A%20%20%3Cpath%20d%3D%22M124.45%2C2.94c12.58-1.69%2C25.75.55%2C36.84%2C6.83%2C10.15%2C5.74%2C20.91%2C10.44%2C32.04%2C13.95%2C6.66%2C1.93%2C13.44%2C3.8%2C19.38%2C7.52%2C14.03%2C8.12%2C24.34%2C22.19%2C28.06%2C37.96%2C3.5%2C11.99%2C8.45%2C23.54%2C14.57%2C34.42%2C10.25%2C17.8%2C10.1%2C41.03-.26%2C58.75-7.25%2C12.71-12.4%2C26.48-16.34%2C40.55-6.32%2C17.83-21.66%2C32.31-39.98%2C37.2-9.92%2C2.8-19.63%2C6.42-28.83%2C11.06-6.17%2C2.88-11.84%2C6.82-18.37%2C8.88-15.92%2C5.21-34.13%2C3.69-48.62-4.88-9.13-5.06-18.68-9.39-28.61-12.63-5.9-2.06-12.1-3.26-17.71-6.08-15.09-7.39-27.08-21.3-31.4-37.62-3.53-12.54-8.48-24.68-14.84-36.05C-.16%2C145.16-.48%2C121.88%2C9.66%2C104c5.21-9.3%2C9.67-19.03%2C13.02-29.16%2C2.07-5.91%2C3.27-12.15%2C6.08-17.8%2C7.2-15.1%2C20.98-26.97%2C37.11-31.54%2C12.93-3.5%2C25.37-8.7%2C37.07-15.21%2C6.6-3.82%2C13.95-6.33%2C21.51-7.35ZM122.27%2C17.79c-7.67%2C1.57-14.09%2C6.24-21.05%2C9.53-7.43%2C3.6-15.1%2C6.7-22.93%2C9.33-5.44%2C1.84-11.18%2C2.91-16.31%2C5.58-10.99%2C5.73-19.8%2C15.93-22.97%2C28.01-3.26%2C11.44-7.55%2C22.58-12.86%2C33.23-2.67%2C5.31-6.12%2C10.29-7.75%2C16.07-2.54%2C8.72-2.53%2C18.2.17%2C26.89%2C1.59%2C5.3%2C4.7%2C9.95%2C7.17%2C14.86%2C5.35%2C10.38%2C9.6%2C21.31%2C12.83%2C32.53%2C3.11%2C12.81%2C12.24%2C23.88%2C24.02%2C29.7%2C4.23%2C2.15%2C8.91%2C3.14%2C13.41%2C4.59%2C8.56%2C2.7%2C16.93%2C6.02%2C24.99%2C9.96%2C6.63%2C3.06%2C12.66%2C7.52%2C19.85%2C9.26%2C11.29%2C2.88%2C23.8%2C1.63%2C33.95-4.29%2C13.37-7.72%2C27.9-13.22%2C42.72-17.44%2C13.43-4.47%2C24.43-15.84%2C28.43-29.44%2C4-14.51%2C9.75-28.54%2C17.17-41.65%2C6.24-11.01%2C7.2-24.7%2C3.14-36.62-1.82-5.47-5.18-10.22-7.68-15.37-4.96-10.25-9.19-20.85-12.19-31.84-3.09-11.85-11.47-22-22.11-27.91-5.99-3.34-12.85-4.43-19.22-6.75-10.5-3.64-20.71-8.12-30.35-13.65-9.72-5.58-21.54-7-32.43-4.58Z%22%20%2F%3E%0A%3C%2Fsvg%3E") !important;
  background-size: cover !important;
}
.vue-advanced-cropper::v-deep .vue-rectangle-stencil {
  display: flex !important;
  border-width: 1px !important;
  border-color: gray !important;
  /* box-shadow: 0 0 5px black, 0 0 5px black; */
}
.vue-advanced-cropper::v-deep .vue-advanced-cropper__foreground {
  opacity: 0;
}
</style>
